import React, { useEffect, useState, useMemo } from 'react';
import { registrationsApi, eventsApi, Event, Registration } from '../api/client';
import { getErrorMessage } from '../utils/errorHandler';
import InvitationManager from './InvitationManager';
import RegistrationHistory from './RegistrationHistory';
import { ExportDialog, ExportOptions } from './ExportDialog';
import { socialCardsApi } from '../api/socialCards';
import ActivityLog from './ActivityLog';

// Import new components
import AttendeeStats from './attendee/AttendeeStats';
import AttendeeActions from './attendee/AttendeeActions';
import ViewModeTabs from './attendee/ViewModeTabs';
import AttendeeFilters, { ViewMode, PipelineStatus, CheckinFilter, SourceFilter } from './attendee/AttendeeFilters';
import AttendeeCard from './attendee/AttendeeCard';
import EditRegistrationModal from './attendee/EditRegistrationModal';

interface AttendeeManagerProps {
  event: Event;
  onUpdate?: () => void;
  showInviteModal?: boolean;
  showExportDialog?: boolean;
  onInviteModalChange?: (show: boolean) => void;
  onExportDialogChange?: (show: boolean) => void;
}

const AttendeeManager: React.FC<AttendeeManagerProps> = ({ 
  event, 
  onUpdate,
  showInviteModal: externalShowInviteModal,
  showExportDialog: externalShowExportDialog,
  onInviteModalChange,
  onExportDialogChange 
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('pipeline');
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [internalShowInviteModal, setInternalShowInviteModal] = useState(false);
  const [internalShowExportDialog, setInternalShowExportDialog] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const showInviteModal = externalShowInviteModal !== undefined ? externalShowInviteModal : internalShowInviteModal;
  const showExportDialog = externalShowExportDialog !== undefined ? externalShowExportDialog : internalShowExportDialog;
  const setShowInviteModal = onInviteModalChange || setInternalShowInviteModal;
  const setShowExportDialog = onExportDialogChange || setInternalShowExportDialog;
  const [showingHistoryFor, setShowingHistoryFor] = useState<number | null>(null);
  const [editingRegistration, setEditingRegistration] = useState<Registration | null>(null);
  
  // Pipeline view filters
  const [pipelineFilter, setPipelineFilter] = useState<'all' | PipelineStatus>('all');
  
  // Check-in view filters
  const [checkinFilter, setCheckinFilter] = useState<CheckinFilter>('not_checked_in');
  
  // Source view filters
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  
  // Sort order state
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  // Unified count calculations
  const counts = useMemo(() => {
    // Pipeline counts
    const invited = registrations.filter(r => r.status === 'invited');
    const opened = registrations.filter(r => r.status === 'opened');
    const registered = registrations.filter(r => 
      r.status === 'pending' || r.status === 'waitlist'
    );
    const approved = registrations.filter(r => r.is_approved);
    const rejected = registrations.filter(r => r.status === 'rejected');
    const declined = registrations.filter(r => r.status === 'declined');
    const cancelled = registrations.filter(r => r.status === 'cancelled');
    const noShow = registrations.filter(r => r.status === 'no_show');
    
    // Check-in counts
    const checkedIn = approved.filter(r => r.status === 'checked_in');
    const notCheckedIn = approved.filter(r => r.status !== 'checked_in');
    
    // Source counts
    const invitationBased = registrations.filter(r => r.is_invitation_based);
    const directRegistrations = registrations.filter(r => !r.is_invitation_based);
    
    // Capacity - use the server figures, which also subtract reserved_tickets
    const capacityPercentage = event.capacity_percentage;
    const availableSpots = event.available_spots;
    
    // Relationship types. Stored values are existing_client / prospective_client / partner / staff;
    // accept the short forms too. Anyone approved without a type (e.g. invited then manually
    // approved) lands in "unspecified" so the mix always adds up to the approved total.
    const relType = (r: Registration) => (r.relationship_type || '').toLowerCase();
    const clients = approved.filter(r => ['existing_client', 'client'].includes(relType(r))).length;
    const prospects = approved.filter(r => ['prospective_client', 'prospect'].includes(relType(r))).length;
    const partners = approved.filter(r => relType(r) === 'partner').length;
    const staff = approved.filter(r => relType(r) === 'staff').length;
    const unspecified = approved.length - clients - prospects - partners - staff;
    
    return {
      // Pipeline
      all: registrations.length,
      invited: invited.length,
      opened: opened.length,
      registered: registered.length,
      approved: approved.length,
      rejected: rejected.length,
      declined: declined.length,
      cancelled: cancelled.length,
      noShow: noShow.length,
      
      // Check-in
      checkedIn: checkedIn.length,
      notCheckedIn: notCheckedIn.length,
      
      // Source
      invitationBased: invitationBased.length,
      directRegistrations: directRegistrations.length,
      
      // Capacity
      capacityPercentage,
      availableSpots,
      
      // Relationship types
      clients,
      prospects,
      partners,
      staff,
      unspecified,
    };
  }, [registrations, event.max_attendees, event.available_spots, event.capacity_percentage]);

  // Load registrations
  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const data = await registrationsApi.getEventRegistrations(event.id);
      setRegistrations(data);
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRegistrations();
  }, [event.id]);

  // Filter registrations based on view mode and filters
  const filteredRegistrations = useMemo(() => {
    let filtered = [...registrations];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.email?.toLowerCase().includes(query) ||
        r.name?.toLowerCase().includes(query) ||
        r.given_name?.toLowerCase().includes(query) ||
        r.family_name?.toLowerCase().includes(query) ||
        r.company?.toLowerCase().includes(query)
      );
    }
    
    // Apply view-specific filters
    if (viewMode === 'pipeline') {
      if (pipelineFilter !== 'all') {
        switch (pipelineFilter) {
          case 'invited':
            filtered = filtered.filter(r => r.status === 'invited');
            break;
          case 'opened':
            filtered = filtered.filter(r => r.status === 'opened');
            break;
          case 'registered':
            filtered = filtered.filter(r => r.status === 'pending' || r.status === 'waitlist');
            break;
          case 'approved':
            filtered = filtered.filter(r => r.is_approved);
            break;
          case 'rejected':
            filtered = filtered.filter(r => r.status === 'rejected');
            break;
          case 'declined':
            filtered = filtered.filter(r => r.status === 'declined');
            break;
          case 'cancelled':
            filtered = filtered.filter(r => r.status === 'cancelled');
            break;
          case 'no_show':
            filtered = filtered.filter(r => r.status === 'no_show');
            break;
        }
      }
    } else if (viewMode === 'checkin') {
      // Check-in view: ticket holders plus anyone tagged as a no-show
      filtered = filtered.filter(r => r.is_approved || r.status === 'no_show');
      
      if (checkinFilter === 'checked_in') {
        filtered = filtered.filter(r => r.status === 'checked_in');
      } else if (checkinFilter === 'no_show') {
        filtered = filtered.filter(r => r.status === 'no_show');
      } else {
        filtered = filtered.filter(r => r.status !== 'checked_in' && r.status !== 'no_show');
      }
    } else if (viewMode === 'source') {
      if (sourceFilter === 'invitation') {
        filtered = filtered.filter(r => r.is_invitation_based);
      } else if (sourceFilter === 'direct') {
        filtered = filtered.filter(r => !r.is_invitation_based);
      }
    }
    
    // Apply sort order
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  }, [registrations, searchQuery, viewMode, pipelineFilter, checkinFilter, sourceFilter, sortOrder]);

  // Handlers
  const handleApprove = async (registrationId: number) => {
    try {
      await registrationsApi.updateRegistrationStatus(registrationId, 'approved');
      await loadRegistrations();
      onUpdate?.();
    } catch (error: any) {
      // 409 = venue capacity reached; the organizer may confirm and override
      if (error?.response?.status === 409) {
        const detail = error.response.data?.detail || 'Venue capacity reached.';
        if (window.confirm(`${detail}\n\nApprove anyway?`)) {
          try {
            await registrationsApi.updateRegistrationStatus(registrationId, 'approved', undefined, true);
            await loadRegistrations();
            onUpdate?.();
            return;
          } catch (overrideError) {
            await loadRegistrations();
            alert(getErrorMessage(overrideError));
            return;
          }
        }
        await loadRegistrations();
        return;
      }
      await loadRegistrations();
      alert(getErrorMessage(error));
    }
  };

  const handleReject = async (registrationId: number) => {
    try {
      await registrationsApi.updateRegistrationStatus(registrationId, 'rejected');
      await loadRegistrations();
      onUpdate?.();
    } catch (error) {
      await loadRegistrations();
      alert(getErrorMessage(error));
    }
  };

  const handleDelete = async (registrationId: number) => {
    if (!window.confirm('Are you sure you want to delete this registration?')) {
      return;
    }
    
    try {
      await registrationsApi.deleteRegistration(registrationId);
      await loadRegistrations();
      onUpdate?.();
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleCheckIn = async (registrationId: number) => {
    try {
      await registrationsApi.checkInRegistration(registrationId);
      await loadRegistrations();
      onUpdate?.();
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleNoShow = async (registrationId: number) => {
    try {
      await registrationsApi.markNoShow(registrationId);
      await loadRegistrations();
      onUpdate?.();
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleUndoNoShow = async (registrationId: number) => {
    try {
      await registrationsApi.undoNoShow(registrationId);
      await loadRegistrations();
      onUpdate?.();
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleCheckOut = async (registrationId: number) => {
    try {
      await registrationsApi.checkOutRegistration(registrationId);
      await loadRegistrations();
      onUpdate?.();
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleResendInvitation = async (registrationId: number) => {
    try {
      await registrationsApi.resendInvitation(registrationId);
      alert('Invitation resent successfully!');
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };
  
  const handleManualApprove = async (registrationId: number) => {
    try {
      await registrationsApi.manuallyApproveInvitation(registrationId);
      await loadRegistrations();
      onUpdate?.();
      alert('Invitation manually approved! Ticket email has been sent.');
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleResendVerification = async (registrationId: number) => {
    try {
      await registrationsApi.resendVerification(registrationId);
      alert('Verification email resent!');
      await loadRegistrations();
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleResendTicket = async (registrationId: number) => {
    try {
      await registrationsApi.resendTicketEmail(registrationId);
      alert('Ticket email resent successfully!');
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleReinstate = async (registrationId: number) => {
    if (!window.confirm('Reinstate this ticket? The attendee is set back to Approved, given a new ticket code, and emailed their ticket.')) {
      return;
    }
    // Same path as approving: the backend re-issues the ticket and applies the capacity check,
    // and handleApprove already offers the override if the venue is full.
    await handleApprove(registrationId);
  };

  const handleCancelTicket = async (registrationId: number) => {
    if (!window.confirm('Are you sure you want to cancel this ticket? This action cannot be undone.')) {
      return;
    }
    
    try {
      await registrationsApi.updateRegistrationStatus(registrationId, 'cancelled');
      await loadRegistrations();
      onUpdate?.();
      alert('Ticket cancelled successfully.');
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleSendSocialCard = async (registrationId: number) => {
    try {
      await socialCardsApi.sendLink(registrationId);
      alert('Social card link sent successfully!');
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleEditSave = async (updates: Partial<Registration>) => {
    if (!editingRegistration) return;
    
    try {
      await registrationsApi.updateRegistration(editingRegistration.id, updates);
      await loadRegistrations();
      setEditingRegistration(null);
      onUpdate?.();
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleExportData = async (options: ExportOptions) => {
    try {
      let blob: Blob;
      let filename: string;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      
      if (options.exportType === 'badges') {
        // Export badges as PDF with QR
        blob = await eventsApi.exportNameBadges(
          event.id, 
          options.badgeOptions?.approvedOnly ?? true,
          options.badgeOptions?.totalBadges
        );
        filename = `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}_name_badges_${timestamp}.pdf`;
      } else if (options.exportType === 'badges-no-qr') {
        // Export badges as PDF without QR
        blob = await eventsApi.exportNameBadgesNoQR(
          event.id, 
          options.badgeOptions?.approvedOnly ?? true,
          options.badgeOptions?.totalBadges
        );
        filename = `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}_name_badges_no_qr_${timestamp}.pdf`;
      } else {
        // Export CSV
        blob = await registrationsApi.exportCSV(event.id, options.statuses, options.includeFields);
        filename = `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}_attendees_${timestamp}.csv`;
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowExportDialog(false);
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading attendees...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {/* Stats Bar */}
      <AttendeeStats 
        counts={counts} 
        maxAttendees={event.max_attendees} 
      />
      
      {/* Actions Bar - don't pass handlers when externally controlled */}
      <AttendeeActions
        viewMode={viewMode}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onInviteClick={externalShowInviteModal === undefined ? (() => setShowInviteModal(true)) : undefined}
        onExportClick={externalShowExportDialog === undefined ? (() => setShowExportDialog(true)) : undefined}
        hasRegistrations={registrations.length > 0}
      />
      
      {/* View Mode Tabs */}
      <ViewModeTabs 
        viewMode={viewMode} 
        onViewModeChange={setViewMode} 
      />
      
      {/* Filter Tabs */}
      {viewMode !== 'activity' && (
        <AttendeeFilters
          viewMode={viewMode}
          pipelineFilter={pipelineFilter}
          checkinFilter={checkinFilter}
          sourceFilter={sourceFilter}
          sortOrder={sortOrder}
          counts={counts}
          onPipelineFilterChange={setPipelineFilter}
          onCheckinFilterChange={setCheckinFilter}
          onSourceFilterChange={setSourceFilter}
          onSortOrderToggle={toggleSortOrder}
        />
      )}
      
      {/* Content */}
      {viewMode === 'activity' ? (
        <ActivityLog eventId={event.id} />
      ) : (
        <div>
          {filteredRegistrations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No attendees found</p>
            </div>
          ) : (
            filteredRegistrations.map(registration => (
              <AttendeeCard
                key={registration.id}
                registration={registration}
                viewMode={viewMode}
                onApprove={() => handleApprove(registration.id)}
                onReject={() => handleReject(registration.id)}
                onCheckIn={() => handleCheckIn(registration.id)}
                onCheckOut={() => handleCheckOut(registration.id)}
                onNoShow={() => handleNoShow(registration.id)}
                onUndoNoShow={() => handleUndoNoShow(registration.id)}
                onDelete={() => handleDelete(registration.id)}
                onEdit={() => setEditingRegistration(registration)}
                onResendInvitation={() => handleResendInvitation(registration.id)}
                onResendVerification={() => handleResendVerification(registration.id)}
                onResendTicket={() => handleResendTicket(registration.id)}
                onSendSocialCard={() => handleSendSocialCard(registration.id)}
                onShowHistory={() => setShowingHistoryFor(registration.id)}
                onManualApprove={() => handleManualApprove(registration.id)}
                onCancelTicket={() => handleCancelTicket(registration.id)}
                onReinstate={() => handleReinstate(registration.id)}
              />
            ))
          )}
        </div>
      )}
      
      {/* Modals */}
      {showInviteModal && (
        <InvitationManager
          event={event}
          onClose={() => {
            setShowInviteModal(false);
            loadRegistrations();
          }}
          onUpdate={loadRegistrations}
        />
      )}
      
      {showExportDialog && (
        <ExportDialog
          isOpen={showExportDialog}
          eventTitle={event.title}
          onExport={handleExportData}
          onClose={() => setShowExportDialog(false)}
        />
      )}
      
      {showingHistoryFor && (
        <RegistrationHistory
          registrationId={showingHistoryFor}
          onClose={() => setShowingHistoryFor(null)}
        />
      )}
      
      {editingRegistration && (
        <EditRegistrationModal
          registration={editingRegistration}
          onSave={handleEditSave}
          onClose={() => setEditingRegistration(null)}
        />
      )}
    </div>
  );
};

export default AttendeeManager;